/**
 * Funções puras da paginação numerada do Design System.
 *
 * Declaradas na pasta do componente (e não em `helper/`) para o DS não depender de
 * helpers de domínio — mesmo critério do `DesignSystem/StatusBadge`.
 */

/** Marcador das reticências na janela de páginas. */
export const ELLIPSIS = 'ellipsis' as const;

export type PageWindowItem = number | typeof ELLIPSIS;

const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/**
 * Formata um inteiro em pt-BR, com ponto de milhar (24087 → "24.087").
 *
 * @param value inteiro a formatar.
 * @returns o número formatado.
 */
export function formatInteger(value: number): string {
  return INTEGER_FORMATTER.format(value);
}

/**
 * Quantidade de páginas para `total` itens com `perPage` por página. Nunca é menor
 * que 1: uma lista vazia ainda tem a página 1.
 *
 * @param total total de itens.
 * @param perPage itens por página.
 * @returns número de páginas (>= 1).
 */
export function getPageCount(total: number, perPage: number): number {
  if (total <= 0 || perPage <= 0) return 1;
  return Math.ceil(total / perPage);
}

/**
 * Janela de páginas visíveis: o conjunto {1, p-1, p, p+1, N}, ordenado, sem
 * repetidos e dentro de [1, N], com reticências onde há páginas omitidas
 * (protótipo `.dc.html:690-691`).
 *
 * @example buildPageWindow(600, 1205) → [1, ELLIPSIS, 599, 600, 601, ELLIPSIS, 1205]
 * @param page página atual (base 1).
 * @param pagesN total de páginas.
 * @returns números de página intercalados com {@link ELLIPSIS}.
 */
export function buildPageWindow(page: number, pagesN: number): PageWindowItem[] {
  const pages = Array.from(new Set([1, page - 1, page, page + 1, pagesN]))
    .filter((n) => n >= 1 && n <= pagesN)
    .sort((a, b) => a - b);

  return pages.reduce<PageWindowItem[]>((acc, n, index) => {
    if (index > 0 && n - pages[index - 1] > 1) acc.push(ELLIPSIS);
    acc.push(n);
    return acc;
  }, []);
}

/**
 * Rótulo do intervalo exibido: "Mostrando 1–20 de 24.087" (travessão U+2013 e
 * milhar com ponto). Com `total` 0, "Mostrando 0–0 de 0".
 *
 * @param page página atual (base 1).
 * @param perPage itens por página.
 * @param total total de itens.
 * @returns o rótulo pronto para exibição.
 */
export function formatRangeLabel(page: number, perPage: number, total: number): string {
  const from = total > 0 ? (page - 1) * perPage + 1 : 0;
  const to = Math.min(total, page * perPage);
  return `Mostrando ${formatInteger(from)}–${formatInteger(to)} de ${formatInteger(total)}`;
}
